import { useEffect, useMemo, useState } from "react";
import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import _ from "lodash";
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  KpiCard,
  Typography,
} from "../../../../components";
import { cacheApi, formatPrice, responseErros } from "../../../../utils";
import { DashboardChart } from "../../../main/components";
import { filtrosUrl } from "../../../../helpers";
import {
  CentroCustoService,
  PlanosFinanceirosService,
  ContasPagarService,
  SessionService,
} from "../../../../services";
import { useCacheContext } from "../../../../contexts/cache.context";
import { DespesasFiltros } from "./components";
import {
  PERIODOS,
  REGIMES,
  filtrosIniciais,
} from "./despesas-por-plano-financeiro";

require("highcharts/modules/accessibility")(Highcharts);

const planosFinanceirosService = new PlanosFinanceirosService();
const centroCustoService = new CentroCustoService();
const contasPagarService = new ContasPagarService();
const sessionService = new SessionService();

const TH_SX = {
  fontWeight: 700,
  color: "#475569",
  borderBottom: "1px solid #e2e8f0",
  fontSize: 13,
  textTransform: "uppercase",
};

const TOTAIS_VAZIOS = {
  totalDespesas: 0,
  mediaMensal: 0,
  maiorPlano: null,
};

const PLANO_POR_MES_VAZIO = {
  meses: [],
  rows: [],
  totaisPorMes: [],
  totalGeral: 0,
};

const METRIC_CARDS = [
  {
    key: "totalDespesas",
    label: "Total de Despesas",
    color: "#d32f2f",
    icon: "ph-receipt",
  },
  {
    key: "mediaMensal",
    label: "Média Mensal — Últimos 12 Meses",
    color: "#1976d2",
    icon: "ph-chart-line",
  },
  {
    key: "maiorPlano",
    label: "Maior Plano Financeiro",
    color: "#f57c00",
    icon: "ph-trend-up",
  },
];

const BASE_CHART_OPTIONS = {
  chart: { backgroundColor: null, style: { fontFamily: "inherit" } },
  title: { text: "" },
  credits: { enabled: false },
  legend: {
    itemStyle: { color: "#505053", fontWeight: 400 },
    itemHoverStyle: { color: "#334155" },
  },
};

function TopCategoriasBar({ data, loading }) {
  const options = useMemo(() => {
    const opts = {};
    _.merge(opts, BASE_CHART_OPTIONS, {
      chart: { type: "bar", height: 260 },
      xAxis: {
        categories: data.map((d) => d.categoria || d.label || d.descricao),
        labels: { style: { color: "#475569", fontSize: "11px" } },
        lineColor: "#e2e8f0",
        tickColor: "transparent",
      },
      yAxis: {
        title: { text: "" },
        gridLineColor: "#f1f5f9",
        labels: {
          style: { color: "#475569", fontSize: "11px" },
          formatter: function () {
            return this.value >= 1000
              ? `${(this.value / 1000).toFixed(0)}k`
              : this.value;
          },
        },
      },
      tooltip: {
        formatter: function () {
          return `<b>${this.point.category}</b><br/>Despesas: <b>${formatPrice(
            this.y,
          )}</b>`;
        },
      },
      plotOptions: {
        bar: {
          dataLabels: { enabled: false },
          borderWidth: 0,
          borderRadius: 2,
        },
      },
      series: [
        {
          name: "Despesas",
          data: data.map((d) => d.realizado),
          color: "#2196F3",
          showInLegend: false,
        },
      ],
    });
    return opts;
  }, [data]);

  if (loading) {
    return (
      <Box>
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            height={20}
            sx={{ mb: 1, borderRadius: 1 }}
          />
        ))}
      </Box>
    );
  }

  return <HighchartsReact highcharts={Highcharts} options={options} />;
}

function DistribuicaoCentroDonut({ data, loading }) {
  const options = useMemo(() => {
    const opts = {};
    _.merge(opts, BASE_CHART_OPTIONS, {
      chart: { type: "pie", height: 260 },
      tooltip: {
        pointFormatter: function () {
          return `<span style="color:${this.color}">●</span> ${
            this.name
          }: <b>${formatPrice(this.y)}</b> (${this.percentage.toFixed(
            1,
          )}%)<br/>`;
        },
      },
      plotOptions: {
        pie: {
          innerSize: "50%",
          borderWidth: 2,
          borderColor: "#fff",
          dataLabels: { enabled: false },
          showInLegend: true,
          colors: data.map((d) => d.color),
        },
      },
      series: [
        {
          name: "Despesas",
          colorByPoint: true,
          data: data.map((d) => ({
            name: d.centro,
            y: d.realizado,
            color: d.color,
          })),
        },
      ],
    });
    return opts;
  }, [data]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={260}
      >
        <Skeleton variant="circular" width={180} height={180} />
      </Box>
    );
  }

  return <HighchartsReact highcharts={Highcharts} options={options} />;
}

const DespesasPorPlanoFinanceiroView = () => {
  const { cacheDto, setCacheDto } = useCacheContext();
  const usuario = sessionService.getUsuario();
  const empresa = sessionService.getEmpresa();
  const acessosEmpresas = usuario?.acessosEmpresas || [];
  const filtrosPadrao = {
    ...filtrosIniciais,
    empresas: empresa ? [empresa] : [],
  };

  const [loading, setLoading] = useState(false);
  const [listas, setListas] = useState({
    planosFinanceiros: [],
    centrosCusto: [],
  });
  const [totais, setTotais] = useState(TOTAIS_VAZIOS);
  const [topPlanos, setTopPlanos] = useState([]);
  const [distribuicao, setDistribuicao] = useState([]);
  const [evolucao, setEvolucao] = useState({ categories: [], series: [] });
  const [planoPorMes, setPlanoPorMes] = useState(PLANO_POR_MES_VAZIO);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(filtrosPadrao);
  const [filtros, setFiltros] = useState(filtrosPadrao);

  const centroCustoId = filtros.centroCusto?.id ?? null;
  const planoFinanceiroId = filtros.planoFinanceiro?.id ?? null;
  const empresasIds = (filtros.empresas ?? []).map((item) => item.id);
  const empresasIdsKey = empresasIds.join(",");

  const activeCount =
    (filtros.centroCusto ? 1 : 0) +
    (filtros.planoFinanceiro ? 1 : 0) +
    (acessosEmpresas.length > 0 ? empresasIds.length : 0);

  useEffect(() => {
    buscarPlanoFinanceiro();
    buscarCentroCusto();
  }, []);

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filtros.dataInicio,
    filtros.dataFim,
    centroCustoId,
    planoFinanceiroId,
    empresasIdsKey,
    filtros.agrupamento,
    filtros.regime,
  ]);

  const abrirFiltros = () => {
    setDraft(filtros);
    setOpen(true);
  };

  async function buscarPlanoFinanceiro() {
    const res = await planosFinanceirosService.getAll({ nonPaginated: true });
    if (!res.isAxiosError) {
      setListas((prev) => ({ ...prev, planosFinanceiros: res.data }));
    } else {
      responseErros(res);
    }
  }

  async function buscarCentroCusto() {
    const res = await centroCustoService.getAll({ nonPaginated: true });
    if (!res.isAxiosError) {
      setListas((prev) => ({ ...prev, centrosCusto: res.data }));
    } else {
      responseErros(res);
    }
  }

  function limparResultado() {
    setTotais(TOTAIS_VAZIOS);
    setTopPlanos([]);
    setDistribuicao([]);
    setEvolucao({ categories: [], series: [] });
    setPlanoPorMes(PLANO_POR_MES_VAZIO);
  }

  async function fetchReport() {
    setLoading(true);
    try {
      const label = filtrosUrl.getUrl();
      const cacheKey = `${label}-${filtros.dataInicio}-${filtros.dataFim}-${centroCustoId}-${planoFinanceiroId}-${empresasIdsKey}-${filtros.agrupamento}-${filtros.regime}-despesas-plano`;
      const cacheBody = {
        dto: { cacheDto, setCacheDto },
        timeMinutes: 15,
        apiFunction: contasPagarService.getRelatorioDespesasPlanoFinanceiro({
          dataInicio: filtros.dataInicio,
          dataFim: filtros.dataFim,
          centroCustoId,
          planoFinanceiroId,
          empresasIds: empresasIds.length ? empresasIds : undefined,
          agrupamento: filtros.agrupamento,
          regime: filtros.regime,
        }),
      };
      const result = await cacheApi(cacheKey, cacheBody);
      if (!result || result.isAxiosError) {
        responseErros(result);
        limparResultado();
      } else {
        const data = result.data || result;
        setTotais(data.totais || TOTAIS_VAZIOS);
        setTopPlanos(data.topPlanosFinanceiros || []);
        setDistribuicao(data.distribuicaoCentroCusto || []);
        setEvolucao(data.evolucao || { categories: [], series: [] });
        setPlanoPorMes(data.planoPorMes || PLANO_POR_MES_VAZIO);
      }
    } catch (err) {
      responseErros(err);
    } finally {
      setLoading(false);
    }
  }

  const chartOptions = useMemo(
    () => ({
      chart: { style: { fontFamily: "inherit" } },
      plotOptions: {
        column: { grouping: true, pointPadding: 0.1, borderWidth: 0 },
      },
      yAxis: {
        labels: {
          formatter: function () {
            if (this.value >= 1000) return `${(this.value / 1000).toFixed(0)}k`;
            return `${this.value}`;
          },
        },
      },
      tooltip: {
        shared: true,
        formatter: function () {
          let s = `<b>${this.x}</b><br/>`;
          this.points?.forEach((point) => {
            s += `<span style="color:${point.color}">●</span> ${
              point.series.name
            }: <b>${formatPrice(point.y)}</b><br/>`;
          });
          return s;
        },
      },
    }),
    [],
  );

  const regimeSelecionado = REGIMES.find((r) => r.value === filtros.regime);
  const { meses, rows: linhasPlano, totaisPorMes, totalGeral } = planoPorMes;

  return (
    <Card className="minimal-layout-card">
      <CardContent className="minimal-layout-card-content rolagem">
        <Box className="w-100 ms-auto" sx={{ pb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2}
            gap={2}
          >
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                Despesas por Plano Financeiro
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {`Organizado por ${regimeSelecionado?.descricao}`}
              </Typography>
            </Box>

            <Badge
              badgeContent={activeCount}
              color="primary"
              overlap="rectangular"
            >
              <Button
                variant="outlined"
                color="primary"
                onClick={abrirFiltros}
                startIcon={<i className="ph-fill ph-sliders-horizontal" />}
                sx={{ whiteSpace: "nowrap" }}
              >
                Filtros
              </Button>
            </Badge>
          </Box>

          <Box mb={3}>
            <DespesasFiltros
              filtros={filtros}
              filtrosPadrao={filtrosPadrao}
              onAplicar={setFiltros}
              listas={listas}
              acessosEmpresas={acessosEmpresas}
              draft={draft}
              setDraft={setDraft}
              open={open}
              setOpen={setOpen}
            />
          </Box>

          <Grid container spacing={2} mb={3}>
            {METRIC_CARDS.map((card) => (
              <Grid item xs={12} sm={6} md={4} key={card.key}>
                <KpiCard
                  title={card.label}
                  value={formatPrice(
                    (card.key === "maiorPlano"
                      ? totais.maiorPlano?.valor
                      : totais[card.key]) ?? 0,
                  )}
                  subtitle={
                    card.key === "maiorPlano"
                      ? totais.maiorPlano?.categoria
                      : null
                  }
                  color={card.color}
                  icon={card.icon}
                  loading={loading}
                />
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2.5} mb={3}>
            <Grid item xs={12} md={7}>
              <Card
                sx={{
                  borderRadius: 3,
                  border: "1px solid #f0f0f0",
                  height: "100%",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, mb: 2 }}
                  >
                    Top Planos Financeiros
                  </Typography>
                  <TopCategoriasBar data={topPlanos} loading={loading} />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={5}>
              <Card
                sx={{
                  borderRadius: 3,
                  border: "1px solid #f0f0f0",
                  height: "100%",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, mb: 2 }}
                  >
                    Distribuição por Centro de Custo
                  </Typography>
                  <DistribuicaoCentroDonut
                    data={distribuicao}
                    loading={loading}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card
            sx={{
              borderRadius: 3,
              border: "1px solid #f0f0f0",
              mb: 3,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
                flexWrap="wrap"
                gap={1.5}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Evolução das Despesas
                </Typography>
                <Box display="flex" gap={1}>
                  {PERIODOS.map((periodo) => (
                    <Button
                      key={periodo.value}
                      onClick={() => {
                        setFiltros((prev) => ({
                          ...prev,
                          agrupamento: periodo.value,
                        }));
                      }}
                      variant={
                        filtros.agrupamento === periodo.value
                          ? "contained"
                          : "outlined"
                      }
                      disableElevation
                      sx={{
                        textTransform: "none",
                        fontWeight:
                          filtros.agrupamento === periodo.value ? 700 : 400,
                      }}
                    >
                      {periodo.label}
                    </Button>
                  ))}
                </Box>
              </Box>

              {loading ? (
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={300}
                  sx={{ borderRadius: 2 }}
                />
              ) : (
                <DashboardChart
                  loading={loading}
                  data={evolucao}
                  heightChart={300}
                  options={chartOptions}
                />
              )}
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 3, border: "1px solid #f0f0f0" }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Despesas por Plano Financeiro x Mês
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Total de {formatPrice(totalGeral)} no período —{" "}
                {regimeSelecionado?.label.toLowerCase()}
              </Typography>

              {loading ? (
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={300}
                  sx={{ borderRadius: 2 }}
                />
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                      <TableRow>
                        <TableCell
                          sx={{
                            ...TH_SX,
                            position: "sticky",
                            left: 0,
                            zIndex: 3,
                            backgroundColor: "#f8fafc",
                            minWidth: "20rem",
                          }}
                        >
                          Plano Financeiro
                        </TableCell>
                        {meses.map((mes) => (
                          <TableCell
                            key={mes.chave}
                            align="right"
                            sx={{ ...TH_SX, minWidth: "8rem" }}
                          >
                            {mes.label}
                          </TableCell>
                        ))}
                        <TableCell
                          align="right"
                          sx={{ ...TH_SX, minWidth: "8rem" }}
                        >
                          Total
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {linhasPlano.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={meses.length + 2}
                            align="center"
                            sx={{ py: 4, color: "#94a3b8", fontSize: 14 }}
                          >
                            Nenhuma despesa encontrada para os filtros
                            selecionados.
                          </TableCell>
                        </TableRow>
                      ) : (
                        <>
                          {linhasPlano.map((linha) => (
                            <TableRow
                              key={linha.planoFinanceiroId ?? "sem-plano"}
                              sx={{
                                "&:hover td": { backgroundColor: "#f0f4f8" },
                              }}
                            >
                              <TableCell
                                sx={{
                                  minWidth: "20rem",
                                  fontWeight: 600,
                                  position: "sticky",
                                  left: 0,
                                  zIndex: 1,
                                  backgroundColor: "#fff",
                                }}
                              >
                                <Box display="flex" alignItems="center" gap={1}>
                                  <span
                                    style={{
                                      width: 4,
                                      minWidth: 4,
                                      height: 18,
                                      borderRadius: 4,
                                      backgroundColor:
                                        linha.planoFinanceiroId === null
                                          ? "#94a3b8"
                                          : "#dc2626",
                                      display: "inline-block",
                                      flexShrink: 0,
                                    }}
                                  />
                                  <span>{linha.planoFinanceiro}</span>
                                </Box>
                              </TableCell>
                              {linha.valores.map((valor, i) => (
                                <TableCell key={i} align="right">
                                  {formatPrice(valor, false)}
                                </TableCell>
                              ))}
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {formatPrice(linha.total)}
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow
                            sx={{
                              backgroundColor: "#f1f5f9",
                              borderTop: "2px solid #e2e8f0",
                            }}
                          >
                            <TableCell
                              sx={{
                                minWidth: "20rem",
                                fontWeight: 700,
                                position: "sticky",
                                left: 0,
                                zIndex: 1,
                                backgroundColor: "#f1f5f9",
                              }}
                            >
                              Total do Período
                            </TableCell>
                            {totaisPorMes.map((valor, i) => (
                              <TableCell
                                key={i}
                                align="right"
                                sx={{ fontWeight: 700 }}
                              >
                                {formatPrice(valor, false)}
                              </TableCell>
                            ))}
                            <TableCell
                              align="right"
                              sx={{ fontWeight: 700, color: "#2563eb" }}
                            >
                              {formatPrice(totalGeral)}
                            </TableCell>
                          </TableRow>
                        </>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DespesasPorPlanoFinanceiroView;
